import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useComingSoon } from "@/components/ComingSoon";
import { getIntegrationSettings, updateIntegrationSettings } from "@/lib/api";
import { Plug, KeyRound, Users, CreditCard, Palette, ExternalLink, Save, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const cs = useComingSoon();
  return (
    <div className="max-w-3xl mx-auto px-8 py-12 fade-in">
      <div className="label-eyebrow mb-3">Workspace</div>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight">Settings</h1>
      <p className="text-sm text-[#5F6677] mt-2">
        QuoWin runs as a single workspace in this MVP. The controls below become live in the next release.
      </p>

      <div className="mt-10 quo-card p-6 space-y-3">
        <Row label="Product" value="QuoWin · AI Proposal Workspace" />
        <Row label="AI Model" value="OpenAI GPT-5.2 (via Emergent universal key)" />
        <Row label="Email sending" value="Live via Resend · quowin.io (verified domain)" />
        <Row label="Storage" value="MongoDB" />
        <Row label="PDF / DOCX" value="ReportLab + python-docx" />
      </div>

      <h2 className="mt-12 font-display text-2xl">Integrations & workspace</h2>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <TranscriptImportCard />
        <ResendDomainCard />
        <ActionCard
          icon={<Palette size={16} className="text-[#011B90]" />}
          title="Brand kit"
          desc="Upload your logo and set proposal colors."
          tid="settings-brand"
          onClick={() => cs("Brand kit", "Upload a logo, choose fonts and set accent colors that flow through your PDF exports and email templates.")}
        />
        <ActionCard
          icon={<Users size={16} className="text-[#011B90]" />}
          title="Invite teammates"
          desc="Add AEs to your workspace."
          tid="settings-invites"
          onClick={() => cs("Team invites", "Multi-seat with role-based permissions is coming soon.")}
        />
        <ActionCard
          icon={<CreditCard size={16} className="text-[#011B90]" />}
          title="Billing"
          desc="Manage plan and usage."
          tid="settings-billing"
          onClick={() => cs("Billing", "Usage-based billing with monthly invoicing is coming soon.")}
        />
      </div>
    </div>
  );
}

function TranscriptImportCard() {
  const [key, setKey] = useState("");
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getIntegrationSettings().then(setSaved).catch(() => {});
  }, []);

  const save = async () => {
    if (!key.trim()) {
      toast.error("Enter an API key first");
      return;
    }
    setSaving(true);
    try {
      await updateIntegrationSettings({ meeting_import_api_key: key.trim() });
      const fresh = await getIntegrationSettings();
      setSaved(fresh);
      setKey("");
      toast.success("Import key saved");
    } catch {
      toast.error("Could not save key");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="transcript-import-card" className="quo-card p-5 flex flex-col gap-3 md:col-span-2">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-[#EDEFF7] grid place-items-center shrink-0">
          <Plug size={16} className="text-[#011B90]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#0B1736]">Meeting Transcript Import API Key</div>
          <div className="text-xs text-[#5F6677] mt-0.5">
            Provider-agnostic — wire up Fireflies, Fathom, Otter or any transcript API here later. Nothing is connected yet.
          </div>
        </div>
        {saved?.has_key && (
          <span data-testid="transcript-import-saved-badge" className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1 shrink-0">
            <CheckCircle2 size={12} /> Key saved
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Input
          data-testid="transcript-import-key-input"
          type="password"
          placeholder={saved?.has_key ? `Saved · ${saved.key_preview}` : "Paste an API key (optional, for future use)"}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          className="h-9 text-sm"
        />
        <Button data-testid="transcript-import-save-btn" onClick={save} disabled={saving} variant="outline" className="h-9 border-[#E8EAF3] text-[#011B90] hover:bg-[#EDEFF7] hover:text-[#011670] shrink-0">
          <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function ResendDomainCard() {
  return (
    <div data-testid="resend-domain-card" className="quo-card p-5 flex items-start gap-3 md:col-span-2">
      <div className="h-9 w-9 rounded-md bg-emerald-50 grid place-items-center shrink-0">
        <CheckCircle2 size={16} className="text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-[#0B1736]">Email sending domain</div>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold uppercase tracking-wide">Verified</span>
        </div>
        <div className="text-xs text-[#5F6677] mt-1 leading-relaxed">
          <span className="font-mono text-[#0B1736]">quowin.io</span> is verified with Resend (DKIM, SPF, tracking CNAME) —
          proposals now send from <span className="font-mono text-[#0B1736]">quote@quowin.io</span> and reach any prospect's inbox.
        </div>
        <a
          href="https://resend.com/domains"
          target="_blank"
          rel="noreferrer"
          data-testid="resend-domains-link"
          className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#011B90] hover:text-[#011670]"
        >
          Manage domains at resend.com/domains <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm border-b border-[#E8EAF3] last:border-0 py-3">
      <span className="text-[#9098A8]">{label}</span>
      <span className="text-[#0B1736]">{value}</span>
    </div>
  );
}

function ActionCard({ icon, title, desc, onClick, tid }) {
  return (
    <div className="quo-card p-5 flex items-start gap-3">
      <div className="h-9 w-9 rounded-md bg-[#EDEFF7] grid place-items-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#0B1736]">{title}</div>
        <div className="text-xs text-[#5F6677] mt-0.5">{desc}</div>
        <Button
          data-testid={tid}
          onClick={onClick}
          variant="outline"
          className="mt-3 h-8 border-[#E8EAF3] text-[#011B90] hover:bg-[#EDEFF7] hover:text-[#011670]"
        >
          Configure
        </Button>
      </div>
    </div>
  );
}
